import React, {ReactNode, useEffect, useState} from 'react';
import {Card, Container, Dropdown, Form, Row, Table} from 'react-bootstrap';
import {InlineAlert} from '@/components/alerts';
import {BaseModal} from 'components/modals';
import {Tooltip} from '@/components/tooltips';
import Icon from '@/components/icons/Icon';
import Button from '@/components/ui/Button';
import PaginationComponent from '@/components/pagination/PaginationComponent';
import axios, {AxiosResponse} from 'axios';
import Col from 'react-bootstrap/Col';
import AwesomeDebouncePromise from 'awesome-debounce-promise';
import SmartFilterBar from '@/components/smart_filter_bar/SmartFilterBar';
import CellLoader from '@/components/loading/CellLoader';
import {serialize} from '@/views/storyboard/utils/helpers';
import BarChartWrapper from '@/views/storyboard/charts/bar/BarChartWrapper';
import QueryHelpModal from '@/components/utils/QueryHelpModal';
import {buildEventDetailURL} from '@/utils/urlBuilder';
import useData from '@/utils/useData';
import useOptimizedConfig from '@/hooks/useOptimizedConfig';
import {usePlatformPermissions} from '@/utils/platformPermissions';
import HelpTextBlock from '@/components/help_text/HelpTextBlock';
import {useStoreActions, useStoreState} from 'store';
import {TextSamplesModalContentsWrapper} from '@/views/storyboard/charts/textsamples/modals/TextSamplesModalContentsWrapper';
import {RegularSearchResultEvent, SearchResult, SearchResultEvent} from 'entities/search';
import {EventTextField, EventTextFieldOption, SortTypeFieldChoice} from 'entities/event';
import {SearchSortType} from '@/views/search/stores/search';
import {ChartOutput} from 'entities/chart';
import {capitalizeFirstLetter} from 'utils/text';
import useConstant from 'use-constant';
import SmartFilters from '@/views/storyboard/filters/SmartFilters';
import Select from 'react-select';
import EventHeader from '@/views/event_detail/EventHeader';
import EventQuickViewHeader from '@/views/event_detail/EventQuickViewHeader';
import {EventActorsList} from '@/views/event_detail/EventActorsList';
import i18n from 'i18n';
import {hasPermission} from '@/utils/permissions/permissionDebug';
import {isModuleEnabled} from '@/utils/modules';
import {isFieldRestricted} from '@/utils/fieldRestrictions';
import {RestrictedFieldIndicator} from '@/components/utils/DataNotAvailable';

const SearchView = React.memo(() => {
    const {userData, loading: loadingUserData} = useOptimizedConfig();
    const platformPermissions = usePlatformPermissions(userData);

    const [modalShown, setModalShown] = useState<boolean>(false);
    const [activeEventId, setActiveEventId] = useState<number | null>(null);
    const [activeEventType, setActiveEventType] = useState<string>('');
    const [activeEventChannel, setActiveEventChannel] = useState<string>('');
    const [activeEventDateTime, setActiveEventDateTime] = useState<string>('');
    const [activeEventAgent, setActiveEventAgent] = useState<any>(null);
    const [activeEventCustomer, setActiveEventCustomer] = useState<any>(null);
    const [activeKeyId, setActiveKeyId] = useState<number | string | null>(null);
    const [activeEvent, setActiveEvent] = useState<SearchResultEvent | null>(null);
    const searchResults = useStoreState((state) => state.search.items);
    const searchResultsCount = useStoreState((state) => state.search.searchResultsCount);
    const searchPagesList = useStoreState((state) => state.search.searchPagesList);
    const storeSearchPagesList = useStoreActions((actions) => actions.search.setSearchPagesList);
    const searchData = useStoreState((state) => state.search.searchData);
    const loadingSearch = useStoreState((state) => state.search.loading);
    const searchCurrentPage = useStoreState((state) => state.search.searchCurrentPage);
    const storeSearchCurrentPage = useStoreActions((actions) => actions.search.setSearchCurrentPage);
    const searchQuery = useStoreState((state) => state.search.searchQuery);
    const queryDuration = useStoreState((state) => state.search.queryDuration);
    const fieldToQuery = useStoreState((state) => state.search.fieldToQuery);
    const sortTypeQuery = useStoreState((state) => state.search.sortTypeQuery);
    const storeSortTypeQuery = useStoreActions((state) => state.search.setSortTypeQuery);
    const viewTypeQuery = useStoreState((state) => state.search.viewTypeQuery);
    const storeViewTypeQuery = useStoreActions((state) => state.search.setViewTypeQuery);
    const searchQueryAllFields = useStoreState((state) => state.search.searchQueryAllFields);
    const storeSearchQueryAllFields = useStoreActions((state) => state.search.setSearchQueryAllFields);
    const searchQueryAdditionalFilters = useStoreState((state) => state.search.searchQueryAdditionalFilters);
    const storeSearchQueryAdditionalFilters = useStoreActions((state) => state.search.setSearchQueryAdditionalFilters);
    const setLoadingSearch = useStoreActions((actions) => actions.search.setLoading);
    const storeSearchResults = useStoreActions((actions) => actions.search.loadSearchResults);
    const storeSearchResultsCount = useStoreActions((actions) => actions.search.setSearchResultsCount);
    const restrictedFieldKeys: string[] = userData?.restrictedFieldKeys || [];
    const storeSearchData = useStoreActions((actions) => actions.search.setSearchData);
    const storeQueryDuration = useStoreActions((actions) => actions.search.setQueryDuration);
    const storeSearchQuery = useStoreActions((state) => state.search.setSearchQuery);
    const storeFieldToQuery = useStoreActions((state) => state.search.setFieldToQuery);
    const params = useStoreState((state) => state.search.params);
    const paramsSetter = useStoreState((actions) => actions.search.paramsSetter);
    const storeParamsSetter = useStoreActions((actions) => actions.search.setParamsSetter);
    const [sortTypeFieldChoices] = useData<SortTypeFieldChoice[]>(window.URLS.sortTypeFieldChoices,
        'sortTypeFieldChoices', 'loadSortTypeFieldChoices');

    const sortTypeI18nKeyByValue: Record<string, string> = {
        relevance: 'jt_sort_by_relevance.text',
        event_datetime: 'jt_sort_by_datetime_descending.text',
        event_datetime_asc: 'jt_sort_by_datetime_ascending.text',
    };

    const getSortTypeLabel = (value: string) => {
        const key = sortTypeI18nKeyByValue[value];

        if (key) {
            return i18n.t(key);
        }

        const match = sortTypeFieldChoices.find((el) => el[0] === value);
        return match ? match[1] : '';
    };
    const appConfig = useStoreState((state) => state.appConfigs.items);
    const userCanAccessEventDetailView = hasPermission('fs.event.read', userData?.permissions, true) || false;
    const userCanAccessSortTypeFieldChoices = hasPermission('fs.application.settings.read', userData?.permissions, true) || false;
    const [enableReinitializeDefault, setEnableReinitializeDefault] = useState(false);
    const paramTypeChoices = {
        query_string: {
            name: 'query_string',
            tempVariable: 'tempSearchQuery',
            variable: searchQuery,
            storeVariable: storeSearchQuery,
            parameterName: 'searchQueryString',
        },
        fields_to_query: {
            name: 'fields_to_query',
            tempVariable: 'tempFieldToQuery',
            variable: fieldToQuery,
            storeVariable: storeFieldToQuery,
            parameterName: 'fieldToQueryString',
        },
        sort_type_query: {
            name: 'sort_type_query',
            tempVariable: 'tempSortTypeQuery',
            variable: sortTypeQuery,
            storeVariable: storeSortTypeQuery,
            parameterName: 'sortTypeQueryString',
        },
        view_type_query: {
            name: 'view_type_query',
            tempVariable: 'tempViewTypeQuery',
            variable: viewTypeQuery,
            storeVariable: storeViewTypeQuery,
            parameterName: 'viewTypeQueryString',
        },
        page: {
            name: 'page',
            tempVariable: 'tempSearchCurrentPage',
            variable: searchCurrentPage,
            storeVariable: storeSearchCurrentPage,
            parameterName: 'page',
        },
        all_fields: {
            name: 'all_fields',
            tempVariable: 'tempSearchQueryAllFields',
            variable: searchQueryAllFields,
            storeVariable: storeSearchQueryAllFields,
            parameterName: 'query',
        },
        additionalFilters: {
            name: 'additionalFilters',
            tempVariable: 'tempSearchQueryAdditionalFilters',
            variable: searchQueryAdditionalFilters,
            storeVariable: storeSearchQueryAdditionalFilters,
            parameterName: 'additionalFilters',
        },
    };
    type ParamTypeChoices = typeof paramTypeChoices;
    type SearchRequestParams = Record<
        ParamTypeChoices[keyof ParamTypeChoices]['parameterName'],
        ParamTypeChoices[keyof ParamTypeChoices]['variable'] | string
    >;
    type URLQueryParams = Record<
        ParamTypeChoices[keyof ParamTypeChoices]['name'],
        ParamTypeChoices[keyof ParamTypeChoices]['variable']
    >;
    type SearchRequest = (url: string, paramsDict: SearchRequestParams) =>
        Promise<AxiosResponse<SearchResult, SearchRequestParams>>;
    const searchUpdateRequest: SearchRequest = (url, paramsDict) =>
        axios.get<SearchResult, AxiosResponse<SearchResult>>
            (url, {params: paramsDict})
            .catch((error) => {
                if (error.response.status === 400) {
                    return error.response;
                }
                throw error;
            });
    const searchUpdateRequestDebounced = useConstant(
        () => AwesomeDebouncePromise<SearchRequest>(searchUpdateRequest, 350),
    );

    useEffect(() => {
        setEnableReinitializeDefault(!searchQuery);
    }, [
        searchQuery,
    ]);

    useEffect(() => {
        if (!paramsSetter) {
            const paramsUrl = new URLSearchParams(window.location.search);
            const entries = paramsUrl.entries();

            for (const entry of entries) {
                const tempNameSpace: Record<
                    ParamTypeChoices[keyof ParamTypeChoices]['tempVariable'],
                    ParamTypeChoices[keyof ParamTypeChoices]['variable']
                > = {};
                const key = entry[0] as keyof ParamTypeChoices;
                const value = entry[1];
                if (Object.keys(paramTypeChoices).includes(key)) {
                    if (key === 'all_fields' || key === 'additionalFilters') {
                        tempNameSpace[paramTypeChoices[key].tempVariable] = JSON.parse(value);
                    } else if (key === 'page') {
                        tempNameSpace[paramTypeChoices[key].tempVariable] = parseInt(value, 10);
                    } else {

                        tempNameSpace[paramTypeChoices[key].tempVariable] = value;
                    }
                    // @ts-expect-error this is a bit too much effort to fix right now
                    (paramTypeChoices[key].storeVariable)(tempNameSpace[paramTypeChoices[key].tempVariable]);
                }
            }
            storeParamsSetter();
        }
        const fetchData = async () => {
            if (searchQuery) {
                setLoadingSearch(true);
                const paramsDict: SearchRequestParams = {};
                Object.values(paramTypeChoices).forEach((params) => {
                    if (params.parameterName === 'query') {
                        paramsDict[params.parameterName] = JSON.stringify(params.variable);
                    } else {
                        paramsDict[params.parameterName] = params.variable;
                    }
                });
                const response = await searchUpdateRequestDebounced(window.URLS.searchQueryApiResource + '/', paramsDict);
                const windowHistoryDict: URLQueryParams = {};
                Object.values(paramTypeChoices).forEach((params) => {
                    if (params.variable) {
                        windowHistoryDict[params.name] = params.variable;
                    }
                });
                window.history.pushState({a: Math.random()}, 'query update', `?${serialize(windowHistoryDict)}`);
                storeSearchResults(response.data.searchResult);
                storeSearchResultsCount(response.data.searchResultCount);
                storeSearchPagesList(response.data.pagesList);
                storeSearchData(response.data.searchData);
                storeQueryDuration(response.data.queryDuration);
                setLoadingSearch(false);
            }
        };
        fetchData();
    }, [
        params,
        searchCurrentPage,
        searchQueryAllFields,
        searchQueryAdditionalFilters,
        setLoadingSearch,
        paramsSetter,
        storeParamsSetter,
        storeSearchQueryAllFields,
        storeSearchQueryAdditionalFilters,
        storeSearchCurrentPage,
        storeFieldToQuery,
        storeSearchQuery,
        searchQuery,
        fieldToQuery,
        sortTypeQuery,
        viewTypeQuery,
        storeSearchResults,
        storeSearchResultsCount,
        storeSearchPagesList,
        storeSearchData,
        storeSortTypeQuery,
        storeViewTypeQuery,
    ]);
    const userCanAccessSearchQueryApi = hasPermission('fs.search.execute', userData?.permissions, true) || false;
    if (!userCanAccessSearchQueryApi && !loadingUserData) {
        return (
            <>
                <HelpTextBlock helpText={i18n.t('ht_perform_search.text')} />

                <div className='alert alert-danger' role='alert'>{i18n.t('tt_you_dont_have_permission_to_access_the_requested_resource.text')}
                </div>
            </>
        );
    }
    let searchResultsDisplay: ReactNode;
    const mainKeys = ['event_datetime', 'channel_name', 'event_type_name'] as const;
    const textKeys = ['event_text'];
    const mainKeysValues: Partial<RegularSearchResultEvent> = {};
    let mainKeysStyle: ReactNode;
    const textKeyValues = {};
    let textKeysStyle: ReactNode;
    const moreKeysValues = {};
    let moreKeysStyle: ReactNode;
    const capitalizeResult = (text: string): string => {
        const newKey = text.replace(/_/g, ' ');
        return capitalizeFirstLetter(newKey);
    };
    const sortTypeQueryChange = (event: any) => {
        storeSortTypeQuery(event.value as SearchSortType);
    };
    const openDetailModal = (event: SearchResultEvent): void => {
        setActiveEventType(event.event_type_name);
        setActiveEventId(event.event_id);
        setActiveEventChannel(event.channel_name);
        setActiveEventDateTime(event.event_datetime);
        const {eventAgent, eventCustomer} = EventActorsList(null, event, userData, restrictedFieldKeys);
        setActiveEventAgent(eventAgent);
        setActiveEventCustomer(eventCustomer);
        setActiveKeyId(event.event_id);
        setModalShown(true);
        setActiveEvent(event);
    };

    const openDetailView = (eventId: number): void => {
        const url = buildEventDetailURL(eventId);
        window.open(url, '_blank');
    };

    let fieldName: EventTextField;
    if (fieldToQuery === EventTextFieldOption.AllFields) {
        fieldName = EventTextFieldOption.EventText;
    } else {
        fieldName = fieldToQuery;
    }
    let modalContents;
    if (modalShown && activeEvent) {
        modalContents = (
            <TextSamplesModalContentsWrapper
                eventData={activeEvent}
                fieldToShow={fieldName}
                autoScrollScope="search"
            />
        );
    }
    if (searchResults === undefined) {
        searchResultsDisplay = (
            <li>
                <InlineAlert
                    variant="error"
                    message={i18n.t('jt_invalid_search.text')}
                />
            </li>
        );
    } else if (searchResults.length) {
        const regularSearchResults = searchResults as RegularSearchResultEvent[];
        searchResultsDisplay = regularSearchResults.map((event: RegularSearchResultEvent) => {
            const {eventAgent, eventCustomer} = EventActorsList(null, event, userData, restrictedFieldKeys);
            const eventKeys = Object.keys(event) as Array<keyof RegularSearchResultEvent>;
            eventKeys.forEach((key) => {
                if (mainKeys.some(k => k === key)) {
                    mainKeysValues[key] = event[key];
                } else if (textKeys.includes(key)) {
                    // @ts-expect-error
                    textKeyValues[key] = event[key];
                } else if (!textKeys.includes(key) && !mainKeys.some(k => k === key)) {
                    // @ts-expect-error
                    moreKeysValues[key] = event[key];
                }
            });

            mainKeysStyle = (
                mainKeys.map((key) => (
                    <span key={key}>
                        <span className="mr-2">{capitalizeResult(key)}: {mainKeysValues[key]}</span><span className="text-muted">| </span>
                    </span>
                ))
            );

            // @ts-ignore
            textKeysStyle = (
                Object.keys(textKeyValues).map((key) => (
                    <div className='mt-3' key={key}>
                        {fieldToQuery === 'all_fields' ? (
                            event.event_text ? (
                                <div
                                    key={event.event_id}

                                    dangerouslySetInnerHTML={{__html: event.event_text}}
                                />
                            ) : (
                                <span className="text-muted">{i18n.t('jt_text_available.text')}</span>
                            )
                        ) : (
                            event.event_highlight_text ? (
                                <div
                                    key={event.event_id}

                                    dangerouslySetInnerHTML={{__html: event.event_highlight_text}}
                                />
                            ) : event[fieldToQuery] ? (
                                <div
                                    key={event.event_id}

                                    // @ts-ignore
                                    dangerouslySetInnerHTML={{__html: event[fieldToQuery]}}
                                />
                            ) : (
                                <span className="text-muted">{i18n.t('jt_text_available.text')}</span>
                            )
                        )}
                    </div>
                ))
            );

            moreKeysStyle = (
                Object.keys(moreKeysValues).map((key) => (
                    <tr key={key}>
                        <td>
                            <small>{key}</small>
                        </td>
                        <td>
                            <small>
                                {
                                    isFieldRestricted(key, restrictedFieldKeys)
                                        ? <RestrictedFieldIndicator />
                                        : moreKeysValues[key as keyof typeof moreKeysValues]
                                            ? (
                                                <span

                                                    dangerouslySetInnerHTML={{
                                                        __html: JSON.stringify(
                                                            moreKeysValues[key as keyof typeof moreKeysValues],
                                                        )
                                                    }}
                                                />
                                            ) : '-'
                                }
                            </small>
                        </td>
                    </tr>
                ))
            );

            return (
                <React.Fragment key={event.event_id}>
                    <li style={{
                        borderBottom: 'none',
                    }}
                    >
                        <div
                            className='card'
                            style={{
                                border: '1px solid rgba(0, 0, 0, 0.125)',
                                marginBottom: '0px',
                            }}
                            data-cy='search-result-card'
                        >
                            {/* search results element header */}
                            <div className='card-body'>
                                <div className="d-flex align-items-center">
                                    <div className="flex-grow-1">
                                        <EventHeader
                                            eventType={event?.event_type_name}
                                            eventId={event?.event_id?.toString() || ''}
                                            channelName={event?.channel_name}
                                            dateTime={event?.event_datetime}
                                            eventAgent={typeof eventAgent === 'object' ? (Array.isArray(eventAgent) ? eventAgent[0]?.agentName || '' : eventAgent?.agentName || '') : (eventAgent || '')}
                                            eventCustomer={(eventCustomer?.customerName || '').trim()}
                                        />
                                    </div>

                                    <Tooltip content={i18n.t('jt_actions.text')} placement="top">
                                        <Dropdown className="d-inline-block ml-auto">
                                            <Dropdown.Toggle
                                                as={Button}
                                                variant="outline-secondary"
                                                size="sm"
                                                icon="mdi-dots-horizontal"
                                                className="dropdown-toggle-no-caret"
                                                data-cy="search-actions-toggle"
                                                id="search-actions-dropdown"
                                            />

                                            <Dropdown.Menu align="end">
                                                <Dropdown.Item
                                                    onClick={() => openDetailModal(event)}
                                                    data-cy="search-query-detail-view-btn"
                                                    className="text-body"
                                                >
                                                    <Icon name="mdi-minus-box-multiple" className="mr-2" /> {i18n.t('jt_quick_view.text')}
                                                </Dropdown.Item>

                                                <Dropdown.Item
                                                    onClick={() => openDetailView(event.event_id)}
                                                    data-cy="search-query-view-event-btn"
                                                    className="text-body"
                                                >
                                                    <Icon name="mdi-eye" className="mr-2" /> {i18n.t('jt_event_details.text')}
                                                </Dropdown.Item>
                                            </Dropdown.Menu>
                                        </Dropdown>
                                    </Tooltip>
                                </div>
                                <div className='card-text pt-2'>{textKeysStyle}</div>
                            </div>
                        </div>
                    </li>
                </React.Fragment>
            );
        });
    } else {
        searchResultsDisplay = (
            <li>
                <InlineAlert
                    variant="warning"
                    message={i18n.t('jt_results_found.text')}
                />
            </li>
        );
    }
    const pageChanged = (pageNumber: number) => {
        storeSearchCurrentPage(pageNumber);
    };
    let graphDisplay;
    if (searchData === undefined) {
        graphDisplay = null;
    } else if (searchData.length) {
        graphDisplay = (
            <div className='search-results-chart-settings text-center'>
                <h4 className='card-title'>{i18n.t('jt_chart.text')}</h4>
                <BarChartWrapper
                    dataLooselyTyped={searchData}
                    chartQuery={{
                        query_string: searchQuery,
                        output: ChartOutput.TimelineByDate,
                    }}
                    fieldToQuery={fieldToQuery}
                    searchViewCheck={true}
                    queryDuration={queryDuration}
                />
            </div>
        );
    } else {
        graphDisplay = null;
    }
    let resultsDisplay;
    if (!loadingSearch) {
        if (!searchQuery) {
            resultsDisplay = (
                <div className='p-2 card text-center'>
                    <div className='card-body'>
                        <h4 className='card-title'>{i18n.t('jt_start.text')}</h4>
                        <p className='card-text'>{i18n.t('jt_type_something.text')}</p>
                    </div>
                </div>
            );
        } else {
            resultsDisplay = (
                <div className='p-1 card' id='search-results-card'>
                    <div className='card-body'>
                        {graphDisplay}
                        <Row>
                            <Col xs={9}>
                                <h4 className='card-title'>{i18n.t('jt_search_results.text')}</h4>
                                <h6 className='card-subtitle'>
                                    {
                                        searchResultsCount ? (
                                            `${searchResultsCount} ${i18n.t('jt_search_results.text')} (${queryDuration} ${i18n.t('search_seconds.text')})`
                                        ) : ''
                                    }
                                </h6>
                            </Col>
                            <Col xs={3}>
                                <Form.Group className="mb-3" controlId='sortType' >
                                    {
                                        userCanAccessSortTypeFieldChoices ? (
                                            <Select
                                                value={{
                                                    value: sortTypeQuery,
                                                    label: getSortTypeLabel(sortTypeQuery),
                                                }}
                                                onChange={(event) => sortTypeQueryChange(event)}
                                                name="sortType"
                                                options={sortTypeFieldChoices.map((el) => ({
                                                    value: el[0],
                                                    label: getSortTypeLabel(el[0]),
                                                }))}
                                                data-cy="chart-settings-form-fields-to-query"
                                            />
                                        ) : (
                                            <Tooltip
                                                content={i18n.t('tt_you_dont_have_permission_to_access_the_requested_resource.text')}
                                                placement='top'
                                            >
                                                <Form.Control
                                                    as='select'
                                                    name='sortType'
                                                    disabled
                                                />
                                            </Tooltip>
                                        )
                                    }
                                </Form.Group>
                            </Col>
                        </Row>
                        <ul className='search-listing' data-cy='search-results-list'>
                            {searchResultsDisplay}
                        </ul>
                        <BaseModal
                            isOpen={modalShown}
                            onClose={() => setModalShown(false)}
                            title=""
                            hideHeader={true}
                        >
                            <EventQuickViewHeader
                                eventType={activeEventType}
                                eventId={activeEventId?.toString() || ''}
                                channelName={activeEventChannel}
                                dateTime={activeEventDateTime}
                                eventAgent={typeof activeEventAgent === 'object'
                                    ? (Array.isArray(activeEventAgent)
                                        ? activeEventAgent[0]?.agentName || ''
                                        : activeEventAgent?.agentName || '')
                                    : (activeEventAgent || '')}
                                eventCustomer={(activeEventCustomer?.customerName || '').trim()}
                                canOpenDetailView={userCanAccessEventDetailView && !!activeEventId}
                                detailUrl={activeEventId ? buildEventDetailURL(activeEventId) : undefined}
                                onClose={() => setModalShown(false)}
                            />
                            <hr className="my-0" />
                            <div className="modal-body">
                                <Col>
                                    {modalContents}
                                </Col>
                            </div>
                        </BaseModal>
                        <PaginationComponent
                            pages={searchPagesList}
                            currentPage={searchCurrentPage}
                            onPageChange={pageChanged}
                        />
                    </div>
                </div >
            );
        }
    } else if (loadingSearch) {
        resultsDisplay = (<CellLoader fullHeight={true} />);
    }

    if (!loadingUserData && userData) {

        let specialTimeBasedMessage = null;

        // Time-based access limit in days (from appConfig store or global FCONST)
        const limitDays: number =
            // eslint-disable-next-line @typescript-eslint/no-explicit-any
            ((appConfig as any)?.FS_DATA_ACCESS_LIMIT_LAST_DAYS ??
                (window as any).FCONST?.FS_DATA_ACCESS_LIMIT_LAST_DAYS ??
                0);
        // Split the date string into day, month, and year - with null safety
        if (!userData?.sessionStartCompDate) {
            console.warn('🔍 SearchView: sessionStartCompDate is missing');
            return resultsDisplay;
        }
        const dateParts = userData.sessionStartCompDate.split('/');
        const day = dateParts[0];
        const month = dateParts[1];
        const year = dateParts[2];

        // Create a new date string in the "YYYY/MM/DD" format
        const outputDateString = `${year}/${month}/${day}`;

        // Convert sessionStartCompDate to a JavaScript Date object
        const sessionStartDate = new Date(outputDateString);

        // Calculate the limit date based on the configured limit in days
        const limitDate = new Date();

        // Set the time to 00:00:00
        limitDate.setHours(0, 0, 0, 0);

        limitDate.setDate(limitDate.getDate() - limitDays);

        // Compare session start date with the limit date - with null safety
        if (isModuleEnabled(userData, 'addon-time-based-access') && limitDays > 0 && sessionStartDate < limitDate) {
            specialTimeBasedMessage = (
                <div className="alert alert-warning">
                    {i18n.t('jt_access_to_data_limited_to_the_last.text', {days: limitDays})}
                </div>
            );
        }

        return (
            <>
                {
                    platformPermissions.canAccessOlderDataThanLimit ?
                        null : (
                            specialTimeBasedMessage
                        )
                }
                {
                    userData?.indexAnonymized && (
                        <div className='alert alert-warning'>
                            {i18n.t('jt_the_data.text')}
                        </div>
                    )
                }
                {
                    <HelpTextBlock helpText={i18n.t('ht_perform_search.text')} />
                }
                <Card id='filters-card'>
                    <Card.Body>
                        <SmartFilters enableReinitializeDefault={enableReinitializeDefault} />
                    </Card.Body>
                </Card>
                <SmartFilterBar />
                {resultsDisplay}
                <QueryHelpModal />
            </>
        );
    } else {
        return (
            <CellLoader fullHeight={true} />
        );
    }
});
export default SearchView;